import React from 'react';
import BottomNav from '../../components/BottomNav/BottomNav';
import WelcomeStepBar from '../../components/WelcomeSteps/WelcomeStepBar';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import {
  sendVerificationEmail,
  hasCurrentUserErrors,
  savePreferredNavMode,
  acceptCommunityGuidelines,
} from '../../ducks/user.duck';
import GuidelinesConsentGate from '../../components/GuidelinesConsentGate/GuidelinesConsentGate';
import { logout, authenticationInProgress } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { ownListingForNav } from '../../util/ownListeningProfile';
import PresenceHeartbeat from '../../components/PresenceHeartbeat/PresenceHeartbeat';
import GuestMarketingTopbar from '../../components/GuestMarketingTopbar/GuestMarketingTopbar';
import { isListenerDashboardUser } from '../../util/listenerUserType';
import { reconcileJoinableNotifications } from '../../util/sessionNotifications';
import useSessionNotifications from '../../util/useSessionNotifications';
import { makeTransition } from '../TransactionPage/TransactionPage.duck';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

/**
 * Topbar container component, which is connected to Redux Store.
 * @component
 * @param {Object} props
 * @param {number} props.notificationCount number of notifications
 * @param {Function} props.onLogout logout function
 * @param {Function} props.onManageDisableScrolling manage disable scrolling function
 * @param {Function} props.onResendVerificationEmail resend verification email function
 * @param {Object} props.sendVerificationEmailInProgress send verification email in progress
 * @param {Object} props.sendVerificationEmailError send verification email error
 * @param {boolean} props.hasGenericError has generic error
 * @returns {JSX.Element}
 */
export const TopbarContainerComponent = props => {
  const {
    notificationCount = 0,
    hasGenericError,
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    isAuthenticated,
    dashboardTransactions,
    location,
    onAcceptCommunityGuidelines,
    ...rest
  } = props;
  const rawSessionItems = useSessionNotifications(!!isAuthenticated);
  const sessionItems = reconcileJoinableNotifications(rawSessionItems, {
    transactions: dashboardTransactions,
  });
  const sessionNotificationCount = sessionItems.length;

  return (
    <>
      <PresenceHeartbeat
        enabled={isListenerDashboardUser({ currentUser, currentUserHasListings })}
      />
      {isAuthenticated ? (
        <GuidelinesConsentGate
          currentUser={currentUser}
          pathname={location?.pathname}
          onAccept={() =>
            typeof onAcceptCommunityGuidelines === 'function'
              ? onAcceptCommunityGuidelines()
              : undefined
          }
        />
      ) : null}
      {isAuthenticated ? (
        <Topbar
          notificationCount={notificationCount}
          sessionNotificationCount={sessionNotificationCount}
          showGenericError={hasGenericError}
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserOwnListing={currentUserOwnListing}
          isAuthenticated={isAuthenticated}
          location={location}
          {...rest}
        />
      ) : (
        <GuestMarketingTopbar currentPage={rest.currentPage} />
      )}
      <WelcomeStepBar />
      <BottomNav currentPage={rest.currentPage} />
    </>
  );
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated and isLoggedInAs
  const { isAuthenticated, isLoggedInAs, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    currentUserHasOrders,
    currentUserSaleNotificationCount = 0,
    currentUserOrderNotificationCount = 0,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  const dashboardRefs = [
    ...(state.DashboardPage?.orderRefs || []),
    ...(state.DashboardPage?.saleRefs || []),
  ];
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserOwnListing: ownListingForNav(
      currentUserOwnListing,
      getMarketplaceEntities(state, state.ProfilePage?.userListingRefs || [])
    ),
    currentUserHasOrders,
    notificationCount: currentUserSaleNotificationCount + currentUserOrderNotificationCount,
    saleNotificationCount: currentUserSaleNotificationCount,
    orderNotificationCount: currentUserOrderNotificationCount,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
    dashboardTransactions: getMarketplaceEntities(state, dashboardRefs),
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onSwitchNavMode: mode => dispatch(savePreferredNavMode(mode)),
  onAcceptSale: txId => dispatch(makeTransition(txId, 'transition/accept', {})),
  onDeclineSale: txId => dispatch(makeTransition(txId, 'transition/decline', {})),
  onAcceptCommunityGuidelines: () => dispatch(acceptCommunityGuidelines()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(TopbarContainerComponent);

export default TopbarContainer;
